import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { TestPlan, Feature, Component, PlatformItem, Session, Platform } from '../../types';
import { apiRequest } from '../../utils/api';
import { useToast, showTempMessage } from '../../utils/messages';
import { Toast } from '../../utils/messages';
import { useProduct } from '../../contexts/ProductContext';
import { useMilestones } from '../../utils/milestones';
import { theme } from '../../theme';
import { formatDate } from '../../utils/date';
import { Icons } from '../../utils/icons';
import ConfirmDialog from '../shared/ConfirmDialog';
import TestPlanFormModal from './TestPlanFormModal';
import TestPlanDetails from './TestPlanDetails';
import MilestoneSelector from '../shared/MilestoneSelector';
import SearchFilterBar from '../shared/SearchFilterBar';
import CreateSessionModal from '../sessions/CreateSessionModal';
import '../../App.css';

const TestPlanManagement: React.FC = () => {
  const navigate = useNavigate();
  const { selectedProduct } = useProduct();
  const selectedProductId = selectedProduct?.id || '';
  const [draftTestPlans, setDraftTestPlans] = useState<TestPlan[]>([]);
  const [activeTestPlans, setActiveTestPlans] = useState<TestPlan[]>([]);
  const [completedTestPlans, setCompletedTestPlans] = useState<TestPlan[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<'open' | 'completed'>('open');
  const [selectedTestPlan, setSelectedTestPlan] = useState<TestPlan | null>(null);
  const [sortField, setSortField] = useState<'title' | 'createdAt' | 'updatedAt' | 'milestone'>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [filterText, setFilterText] = useState('');
  const [filterMilestone, setFilterMilestone] = useState<string>('');
  const [filterCompletedFrom, setFilterCompletedFrom] = useState('');
  const [filterCompletedTo, setFilterCompletedTo] = useState('');
  const [features, setFeatures] = useState<Feature[]>([]);
  const [components, setComponents] = useState<Component[]>([]);
  const [platforms, setPlatforms] = useState<PlatformItem[]>([]);
  const milestones = useMilestones(selectedProductId);
  const [loading, setLoading] = useState(true);
  const [editingTestPlan, setEditingTestPlan] = useState<TestPlan | null>(null);
  const [showTestPlanFormModal, setShowTestPlanFormModal] = useState(false);
  const [showCreateFeatureModal, setShowCreateFeatureModal] = useState(false);
  const [showCreateComponentModal, setShowCreateComponentModal] = useState(false);
  const [newFeatureName, setNewFeatureName] = useState('');
  const [newFeatureDescription, setNewFeatureDescription] = useState('');
  const [newComponentName, setNewComponentName] = useState('');
  const [newComponentDescription, setNewComponentDescription] = useState('');
  const [newComponentFeatureId, setNewComponentFeatureId] = useState<string>('');
  const [statusDropdownOpen, setStatusDropdownOpen] = useState<string | null>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const { message, setMessage, showMessage } = useToast();
  const [confirmDeleteTestPlan, setConfirmDeleteTestPlan] = useState<{ testPlanId: string; testPlanTitle: string } | null>(null);
  const [showCreateSessionModal, setShowCreateSessionModal] = useState(false);
  const [sessionInitialData, setSessionInitialData] = useState<{
    productId?: string;
    features?: string[];
    components?: string[];
    platforms?: Platform[];
    referenceUrl?: string;
    testPlanUrl?: string;
    milestone?: string;
  } | undefined>(undefined);
  
  // Close status dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setStatusDropdownOpen(null);
      }
    };
    
    if (statusDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [statusDropdownOpen]);
  

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const platformsData = await apiRequest<PlatformItem[]>('/api/platforms', {}, { useAuth: true });
        const platformsArray = Array.isArray(platformsData) ? platformsData : [];
        setPlatforms(platformsArray);
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, []);

  // Filter platforms by selected product
  const filteredPlatforms = useMemo(() => {
    if (!selectedProductId) return platforms;
    return platforms.filter(platform =>
      !platform.productIds || platform.productIds.length === 0 || platform.productIds.includes(selectedProductId)
    );
  }, [platforms, selectedProductId]);


  // Fetch test plans when product changes
  useEffect(() => {
    if (!selectedProductId) {
      // Only clear if we truly don't have a product selected (not just a temporary empty state)
      // This prevents clearing when modal opens if there's a brief moment where selectedProductId is empty
    if (!selectedProduct) {
      setDraftTestPlans([]);
      setActiveTestPlans([]);
      setCompletedTestPlans([]);
      }
      return;
    }
    const fetchTestPlans = async () => {
      try {
        setLoading(true);
        const [draftData, activeData, completedData] = await Promise.all([
          apiRequest<TestPlan[]>(`/api/testplans?productId=${selectedProductId}&status=draft`, {}, { useAuth: true }),
          apiRequest<TestPlan[]>(`/api/testplans?productId=${selectedProductId}&status=active`, {}, { useAuth: true }),
          apiRequest<TestPlan[]>(`/api/testplans?productId=${selectedProductId}&status=completed`, {}, { useAuth: true })
        ]);
        setDraftTestPlans(draftData || []);
        setActiveTestPlans(activeData || []);
        setCompletedTestPlans(completedData || []);
      } catch (error) {
        console.error('Error fetching test plans:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTestPlans();
  }, [selectedProductId, selectedProduct]);

  // Fetch features and components when product changes
  useEffect(() => {
    if (!selectedProductId) {
      setFeatures([]);
      setComponents([]);
      return;
    }
    const fetchData = async () => {
      try {
        const [featuresData, componentsData] = await Promise.all([
          apiRequest<Feature[]>(`/api/features?productId=${selectedProductId}`, {}, { useAuth: true }),
          apiRequest<Component[]>('/api/components', {}, { useAuth: true })
        ]);
        setFeatures(featuresData || []);
        if (Array.isArray(componentsData)) {
          const featureIds = (featuresData || []).map(f => f.id);
          const filtered = componentsData.filter((c: Component) => featureIds.includes(c.featureId));
          setComponents(filtered);
        }
      } catch (error) {
        console.error('Error fetching features/components:', error);
      }
    };
    fetchData();
  }, [selectedProductId]);

  const handleOpenCreateModal = () => {
    setEditingTestPlan(null);
    setShowTestPlanFormModal(true);
  };

  const handleCloseTestPlanFormModal = () => {
    setShowTestPlanFormModal(false);
    setEditingTestPlan(null);
  };

  const handleEdit = (testPlan: TestPlan) => {
    setEditingTestPlan(testPlan);
    setShowTestPlanFormModal(true);
  };

  const handleDelete = async (testPlanId: string) => {
    const allTestPlans = [...draftTestPlans, ...activeTestPlans, ...completedTestPlans];
    const testPlan = allTestPlans.find(tp => tp.id === testPlanId);
    if (testPlan) {
      setConfirmDeleteTestPlan({ testPlanId, testPlanTitle: testPlan.title });
    }
  };

  const confirmDeleteTestPlanAction = async () => {
    if (!confirmDeleteTestPlan) return;

    const { testPlanId } = confirmDeleteTestPlan;
    try {
      await apiRequest(`/api/testplans/${testPlanId}`, {
        method: 'DELETE'
      }, { useAuth: true });
      showTempMessage(setMessage, { type: 'success', text: 'Test plan deleted successfully' });
      // Refresh all test plan lists
      const [draftData, activeData, completedData] = await Promise.all([
        apiRequest<TestPlan[]>(`/api/testplans?productId=${selectedProductId}&status=draft`, {}, { useAuth: true }),
        apiRequest<TestPlan[]>(`/api/testplans?productId=${selectedProductId}&status=active`, {}, { useAuth: true }),
        apiRequest<TestPlan[]>(`/api/testplans?productId=${selectedProductId}&status=completed`, {}, { useAuth: true })
      ]);
      setDraftTestPlans(draftData || []);
      setActiveTestPlans(activeData || []);
      setCompletedTestPlans(completedData || []);
      setSelectedTestPlan(null);
      setConfirmDeleteTestPlan(null);
    } catch (error: any) {
      showTempMessage(setMessage, { type: 'error', text: error.message || 'Failed to delete test plan' });
      setConfirmDeleteTestPlan(null);
    }
  };

  const handleStatusChange = async (testPlanId: string, newStatus?: 'draft' | 'active' | 'completed') => {
    // If newStatus is not provided, default to 'completed' for backward compatibility
    const status = newStatus || 'completed';
    try {
      await apiRequest(`/api/testplans/${testPlanId}`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
        headers: { 'Content-Type': 'application/json' }
      }, { useAuth: true });
      const statusMessages = {
        draft: 'Test plan moved to draft',
        active: 'Test plan moved to open',
        completed: 'Test plan marked as completed'
      };
      showTempMessage(setMessage, { type: 'success', text: statusMessages[status] });
      // Refresh all test plan lists
      const [draftData, activeData, completedData] = await Promise.all([
        apiRequest<TestPlan[]>(`/api/testplans?productId=${selectedProductId}&status=draft`, {}, { useAuth: true }),
        apiRequest<TestPlan[]>(`/api/testplans?productId=${selectedProductId}&status=active`, {}, { useAuth: true }),
        apiRequest<TestPlan[]>(`/api/testplans?productId=${selectedProductId}&status=completed`, {}, { useAuth: true })
      ]);
      setDraftTestPlans(draftData || []);
      setActiveTestPlans(activeData || []);
      setCompletedTestPlans(completedData || []);
      setSelectedTestPlan(null);
    } catch (error: any) {
      showTempMessage(setMessage, { type: 'error', text: error.message || 'Failed to change test plan status' });
      throw error; // Re-throw so the modal doesn't close on error
    }
  };

  const handleSaveMilestone = async (testPlanId: string, newMilestone: string) => {
    if (!newMilestone.trim()) {
      showTempMessage(setMessage, { type: 'error', text: 'Milestone is required' });
      return;
    }

    try {
      await apiRequest(`/api/testplans/${testPlanId}`, {
        method: 'PUT',
        body: JSON.stringify({ milestone: newMilestone.trim() }),
        headers: { 'Content-Type': 'application/json' }
      }, { useAuth: true });
      
      showTempMessage(setMessage, { type: 'success', text: 'Milestone updated successfully' });
      
      // Refresh all test plan lists
      const [draftData, activeData, completedData] = await Promise.all([
        apiRequest<TestPlan[]>(`/api/testplans?productId=${selectedProductId}&status=draft`, {}, { useAuth: true }),
        apiRequest<TestPlan[]>(`/api/testplans?productId=${selectedProductId}&status=active`, {}, { useAuth: true }),
        apiRequest<TestPlan[]>(`/api/testplans?productId=${selectedProductId}&status=completed`, {}, { useAuth: true })
      ]);
      setDraftTestPlans(draftData || []);
      setActiveTestPlans(activeData || []);
      setCompletedTestPlans(completedData || []);
    } catch (error: any) {
      showTempMessage(setMessage, { type: 'error', text: error.message || 'Failed to update milestone' });
    }
  };

  const handleReopen = async (testPlanId: string) => {
    try {
      await apiRequest(`/api/testplans/${testPlanId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'active' }),
        headers: { 'Content-Type': 'application/json' }
      }, { useAuth: true });
      showTempMessage(setMessage, { type: 'success', text: 'Test plan reopened' });
      // Refresh all test plan lists
      const [draftData, activeData, completedData] = await Promise.all([
        apiRequest<TestPlan[]>(`/api/testplans?productId=${selectedProductId}&status=draft`, {}, { useAuth: true }),
        apiRequest<TestPlan[]>(`/api/testplans?productId=${selectedProductId}&status=active`, {}, { useAuth: true }),
        apiRequest<TestPlan[]>(`/api/testplans?productId=${selectedProductId}&status=completed`, {}, { useAuth: true })
      ]);
      setDraftTestPlans(draftData || []);
      setActiveTestPlans(activeData || []);
      setCompletedTestPlans(completedData || []);
      setSelectedTestPlan(null);
    } catch (error: any) {
      showTempMessage(setMessage, { type: 'error', text: error.message || 'Failed to reopen test plan' });
      throw error; // Re-throw so the modal doesn't close on error
    }
  };

  const handleCreateFeature = async () => {
    if (!newFeatureName.trim() || !selectedProductId) return;

    try {
      const newFeature = await apiRequest<Feature>('/api/features', {
        method: 'POST',
        body: JSON.stringify({
          name: newFeatureName.trim(),
          productId: selectedProductId,
          description: newFeatureDescription.trim() || undefined
        }),
        headers: { 'Content-Type': 'application/json' }
      }, { useAuth: true });

      setFeatures(prev => [...prev, newFeature]);
      setNewFeatureName('');
      setNewFeatureDescription('');
      setShowCreateFeatureModal(false);
      showTempMessage(setMessage, { type: 'success', text: 'Feature created successfully' });
    } catch (error: any) {
      showTempMessage(setMessage, { type: 'error', text: error.message || 'Failed to create feature' });
    }
  };

  const handleCreateComponent = async () => {
    if (!newComponentName.trim() || !newComponentFeatureId) return;

    try {
      const newComponent = await apiRequest<Component>('/api/components', {
        method: 'POST',
        body: JSON.stringify({
          name: newComponentName.trim(),
          featureId: newComponentFeatureId,
          description: newComponentDescription.trim() || undefined
        }),
        headers: { 'Content-Type': 'application/json' }
      }, { useAuth: true });

      setComponents(prev => [...prev, newComponent]);
      setNewComponentName('');
      setNewComponentDescription('');
      setNewComponentFeatureId('');
      setShowCreateComponentModal(false);
      showTempMessage(setMessage, { type: 'success', text: 'Component created successfully' });
    } catch (error: any) {
      showTempMessage(setMessage, { type: 'error', text: error.message || 'Failed to create component' });
    }
  };

  const handleRunSession = async (testPlan: TestPlan) => {
    if (!testPlan.components || testPlan.components.length === 0) {
      showTempMessage(setMessage, { type: 'error', text: 'No components selected for this test plan' });
      return;
    }

    // Get unique feature IDs from components
    const componentFeatures = new Set<string>();
    testPlan.components.forEach(componentId => {
      const component = components.find(c => c.id === componentId);
      if (component) {
        componentFeatures.add(component.featureId);
      }
    });

    // Use the test plan details URL as the test plan URL (separate from reference URL)
    const testPlanUrl = `${window.location.origin}/testplan/${testPlan.id}`;

    // Set initial data and open the create session modal
    setSessionInitialData({
      productId: testPlan.productId,
      features: Array.from(componentFeatures),
      components: testPlan.components,
      platforms: testPlan.testingPlan || [],
      testPlanUrl: testPlanUrl,
      milestone: testPlan.milestone
    });
    setShowCreateSessionModal(true);
  };


  const sortTestPlans = (testPlansList: TestPlan[]): TestPlan[] => {
    const sorted = [...testPlansList].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'createdAt':
          aValue = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bValue = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          break;
        case 'updatedAt':
          aValue = a.updatedAt ? new Date(a.updatedAt).getTime() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
          bValue = b.updatedAt ? new Date(b.updatedAt).getTime() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
          break;
        case 'milestone':
          aValue = a.milestone || '';
          bValue = b.milestone || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const filterTestPlans = (testPlansList: TestPlan[]): TestPlan[] => {
    let filtered = testPlansList;

    // Filter by milestone if selected
    if (filterMilestone) {
      filtered = filtered.filter((testPlan: TestPlan) => 
        testPlan.milestone && testPlan.milestone.toLowerCase() === filterMilestone.toLowerCase()
      );
    }

    // Filter by completed date range (uses updatedAt as completion date approximation)
    if (filterCompletedFrom || filterCompletedTo) {
      filtered = filtered.filter((testPlan: TestPlan) => {
        // Use updatedAt as completion date (gets set when status changes to completed)
        const dateToCheck = testPlan.updatedAt || testPlan.createdAt;
        if (!dateToCheck) return false;
        const completedDate = new Date(dateToCheck);
        completedDate.setHours(0, 0, 0, 0); // Normalize to start of day
        
        if (filterCompletedFrom) {
          const fromDate = new Date(filterCompletedFrom);
          fromDate.setHours(0, 0, 0, 0);
          if (completedDate < fromDate) return false;
        }
        
        if (filterCompletedTo) {
          const toDate = new Date(filterCompletedTo);
          toDate.setHours(23, 59, 59, 999); // End of day
          if (completedDate > toDate) return false;
        }
        
        return true;
      });
    }

    // Filter by text search
    if (filterText.trim()) {
      const filter = filterText.toLowerCase();
      filtered = filtered.filter((testPlan: TestPlan) => {
        return (
          testPlan.title.toLowerCase().includes(filter) ||
          (testPlan.userStory && testPlan.userStory.toLowerCase().includes(filter)) ||
          (testPlan.requirements && testPlan.requirements.toLowerCase().includes(filter)) ||
          (testPlan.milestone && testPlan.milestone.toLowerCase().includes(filter)) ||
          (testPlan.testingPlan && testPlan.testingPlan.some((platform: string) => platform.toLowerCase().includes(filter)))
        );
      });
    }

    return filtered;
  };

  const getDisplayedTestPlans = (testPlansList: TestPlan[]): TestPlan[] => {
    const filtered = filterTestPlans(testPlansList);
    return sortTestPlans(filtered);
  };

  // Get available milestones from current status's test plans
  const getAvailableMilestones = (): string[] => {
    const currentTestPlans = selectedStatus === 'open' ? [...draftTestPlans, ...activeTestPlans] : completedTestPlans;
    const milestones = new Set<string>();
    currentTestPlans.forEach(tp => {
      if (tp.milestone && tp.milestone.trim()) {
        milestones.add(tp.milestone);
      }
    });
    return Array.from(milestones).sort();
  };

  if (loading) {
    return <div className="form-container">Loading...</div>;
  }

  return (
    <div className="form-container">
      <Toast message={message} />
      
      <div className="page-header">
        <div style={{ flex: 1 }}>
          <h1 className="page-title">Test Plan Management</h1>
        </div>
        
        {selectedProductId && (
          <button
            onClick={handleOpenCreateModal}
            className="btn btn-primary"
          >
            Create New Test Plan
          </button>
        )}
      </div>

      {selectedProduct && (
        <>
          <div className="tabs">
            <button
              className={`tab ${selectedStatus === 'open' ? 'active' : ''}`}
              onClick={() => setSelectedStatus('open')}
            >
              Open ({draftTestPlans.length + activeTestPlans.length})
            </button>
            <button
              className={`tab ${selectedStatus === 'completed' ? 'active' : ''}`}
              onClick={() => setSelectedStatus('completed')}
            >
              Completed ({completedTestPlans.length})
            </button>
          </div>

          {/* Sort and Filter Controls */}
          <SearchFilterBar
            context="testPlans"
            sortField={sortField}
            sortDirection={sortDirection}
            filterText={filterText}
            filterMilestone={filterMilestone}
            availableMilestones={getAvailableMilestones()}
            milestoneMetadata={milestones}
            onSortFieldChange={(field) => setSortField(field as typeof sortField)}
            onSortDirectionChange={setSortDirection}
            onFilterTextChange={setFilterText}
            onFilterMilestoneChange={setFilterMilestone}
            showCompletedDateFilter={selectedStatus === 'completed'}
            filterCompletedFrom={filterCompletedFrom}
            filterCompletedTo={filterCompletedTo}
            onFilterCompletedFromChange={setFilterCompletedFrom}
            onFilterCompletedToChange={setFilterCompletedTo}
          />

          <div className="session-grid">
            {(() => {
              const currentTestPlans = selectedStatus === 'open' ? [...draftTestPlans, ...activeTestPlans] : completedTestPlans;
              const displayed = getDisplayedTestPlans(currentTestPlans);
              
              if (displayed.length === 0) {
                return (
                  <div className="card">
                    <p>
                      {filterText 
                        ? 'No test plans match your filter.' 
                        : selectedStatus === 'open'
                          ? 'No open test plans. Create your first test plan above.'
                          : 'No completed test plans yet.'}
                    </p>
                  </div>
                );
              }

              return displayed.map(testPlan => {
                const isSelected = selectedTestPlan?.id === testPlan.id;
                
                return (
                  <div
                    key={testPlan.id}
                    className="card"
                    style={{
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      border: isSelected ? `2px solid ${theme.cyanGlow}` : `1px solid ${theme.gridLine}`,
                      backgroundColor: isSelected ? theme.cyanTransparent : theme.surface,
                      boxShadow: isSelected ? theme.borderGlow : 'none',
                      overflow: 'hidden',
                      wordWrap: 'break-word',
                    }}
                    onClick={() => {
                      setSelectedTestPlan(testPlan);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = isSelected ? theme.borderGlow : '0 2px 4px rgba(0,0,0,0.1)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    {/* Header - ID and Status on same line */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <div style={{ fontSize: '0.75rem', color: theme.textMuted, fontWeight: '600', fontFamily: 'monospace', letterSpacing: '0.5px' }}>
                          {testPlan.id}
                        </div>
                      <div ref={statusDropdownOpen === testPlan.id ? statusDropdownRef : null} style={{ position: 'relative', flexShrink: 0 }}>
                        <button
                          className={`status-badge status-${testPlan.status || 'active'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setStatusDropdownOpen(statusDropdownOpen === testPlan.id ? null : testPlan.id);
                          }}
                          style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            transition: 'all 0.2s',
                            border: 'none',
                            padding: '0.375rem 0.75rem',
                            borderRadius: '4px',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            position: 'relative',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '0.9';
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = '1';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          {(testPlan.status || 'active').toUpperCase()}
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{
                              marginLeft: '0.25rem',
                              transition: 'transform 0.2s',
                              transform: statusDropdownOpen === testPlan.id ? 'rotate(180deg)' : 'rotate(0deg)',
                              flexShrink: 0,
                            }}
                          >
                            <path
                              d="M8 11L3 6h10l-5 5z"
                              fill={(() => {
                                const status = testPlan.status || 'active';
                                // Get base color from status and apply slight offset for visibility
                                // Using slightly darker versions from the same palette
                                if (status === 'draft') {
                                  // Warning color - use primary-dim equivalent for warning (slightly darker)
                                  return theme.warning; // Use warning directly, it's already in the palette
                                } else if (status === 'active') {
                                  // Cyan glow - use primary-dim (slightly darker cyan)
                                  return theme.primaryDim || theme.cyanDim;
                                } else if (status === 'completed') {
                                  // Success color - use success directly
                                  return theme.success;
                                }
                                return theme.cyanGlow; // Default fallback
                              })()}
                            />
                          </svg>
                        </button>
                        {statusDropdownOpen === testPlan.id && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '100%',
                              right: 0,
                              marginTop: '0.25rem',
                              backgroundColor: theme.surface,
                              border: `1px solid ${theme.gridLine}`,
                              borderRadius: '4px',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                              zIndex: 1000,
                              minWidth: '120px',
                              overflow: 'hidden',
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {['draft', 'active', 'completed'].map((status) => (
                              <button
                                key={status}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const newStatus = status as 'draft' | 'active' | 'completed';
                                  if (newStatus !== testPlan.status) {
                                    handleStatusChange(testPlan.id, newStatus);
                                  }
                                  setStatusDropdownOpen(null);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '0.625rem 0.75rem',
                                  textAlign: 'left',
                                  border: 'none',
                                  background: testPlan.status === status ? theme.cyanTransparent : 'transparent',
                                  color: theme.textPrimary,
                                  cursor: 'pointer',
                                  fontSize: '0.875rem',
                                  fontWeight: testPlan.status === status ? 600 : 400,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  transition: 'background-color 0.15s',
                                }}
                                onMouseEnter={(e) => {
                                  if (testPlan.status !== status) {
                                    e.currentTarget.style.backgroundColor = theme.surfaceElevated;
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (testPlan.status !== status) {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }
                                }}
                              >
                                <span className={`status-badge status-${status}`} style={{ 
                                  fontSize: '0.625rem',
                                  padding: '0.125rem 0.375rem',
                                  borderRadius: '2px',
                                  pointerEvents: 'none'
                                }}>
                                  {status.toUpperCase()}
                                </span>
                                {testPlan.status === status && (
                                  <span style={{ marginLeft: 'auto', fontSize: '0.875rem' }}>✓</span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Title */}
                    <div style={{ fontSize: '1rem', fontWeight: '600', color: theme.textPrimary, marginBottom: '0.5rem', lineHeight: '1.3' }}>
                          {testPlan.title}
                        </div>

                    {/* Milestone - Inline Editable Dropdown */}
                    <div style={{ marginBottom: '0.5rem', lineHeight: '1.3' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ color: theme.cyanGlow, fontSize: '0.875rem', fontWeight: '600' }}>Milestone:</span>
                        <div style={{ flex: 1, maxWidth: '300px' }}>
                          <MilestoneSelector
                            value={testPlan.milestone || ''}
                            onChange={(milestone) => {
                              handleSaveMilestone(testPlan.id, milestone);
                            }}
                            productId={selectedProductId}
                            placeholder="Click to add milestone"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Created Date */}
                    {testPlan.createdAt && (
                      <div style={{ color: theme.textSecondary, fontSize: '0.875rem', marginBottom: '0.5rem', lineHeight: '1.3' }}>
                        Created: {formatDate(testPlan.createdAt, 'MMM dd, yyyy')}
                      </div>
                    )}

                    {/* Action Buttons */}
                    {(testPlan.status === 'draft' || testPlan.status === 'active') ? (
                      <div style={{ 
                        marginTop: '0.75rem', 
                        paddingTop: '0.75rem',
                        borderTop: `1px solid ${theme.gridLine}`,
                        display: 'flex', 
                        gap: '0.5rem', 
                        alignItems: 'center',
                        flexWrap: 'nowrap'
                      }}>
                        {testPlan.status === 'active' && testPlan.components && testPlan.components.length > 0 && (
                          <button
                            className="btn btn-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRunSession(testPlan);
                            }}
                            style={{ flex: 1 }}
                          >
                            Create Session
                          </button>
                        )}
                        <button
                          className="btn btn-secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(testPlan);
                          }}
                          style={{
                            padding: '0.5rem',
                            minWidth: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          className="btn btn-secondary btn-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(testPlan.id);
                          }}
                          style={{
                            padding: '0.5rem',
                            minWidth: '2.25rem',
                            minHeight: '2.25rem',
                            width: '2.25rem',
                            height: '2.25rem',
                            fontSize: '1rem',
                            color: theme.danger || '#dc2626',
                            lineHeight: '1',
                            flexShrink: 0
                          }}
                          title="Delete"
                        >
                          {Icons.delete}
                        </button>
                      </div>
                    ) : (
                      <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: theme.textSecondary }}>
                        {isSelected ? 'Click to hide details' : 'Click to view details'}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>

          {/* Test Plan Details Modal */}
          {selectedTestPlan && (
            <TestPlanDetails
              milestones={milestones}
              scope={selectedTestPlan}
              onClose={() => setSelectedTestPlan(null)}
              features={features}
              components={components}
              projectCode={selectedProduct?.projectId}
              onEdit={handleEdit}
              onComplete={handleStatusChange}
              onStatusChange={handleStatusChange}
              onReopen={handleReopen}
              onDelete={handleDelete}
              onRunSession={handleRunSession}
            />
          )}
        </>
      )}

      {/* Test Plan Form Modal */}
      {showTestPlanFormModal && (
        <TestPlanFormModal
          isOpen={showTestPlanFormModal}
          onClose={handleCloseTestPlanFormModal}
          editingScope={editingTestPlan}
          productId={selectedProductId}
          features={features}
          components={components}
          platforms={filteredPlatforms}
          onSave={async () => {
            // Refresh all test plan lists
            const [draftData, activeData, completedData] = await Promise.all([
              apiRequest<TestPlan[]>(`/api/testplans?productId=${selectedProductId}&status=draft`, {}, { useAuth: true }),
              apiRequest<TestPlan[]>(`/api/testplans?productId=${selectedProductId}&status=active`, {}, { useAuth: true }),
              apiRequest<TestPlan[]>(`/api/testplans?productId=${selectedProductId}&status=completed`, {}, { useAuth: true })
            ]);
            setDraftTestPlans(draftData || []);
            setActiveTestPlans(activeData || []);
            setCompletedTestPlans(completedData || []);
          }}
          onFeaturesUpdated={async () => {
            // Refresh features when a new one is created
            const featuresData = await apiRequest<Feature[]>(`/api/features?productId=${selectedProductId}`, {}, { useAuth: true });
            setFeatures(featuresData || []);
          }}
          onComponentsUpdated={async () => {
            // Refresh components when a new one is created
            const componentsData = await apiRequest<Component[]>('/api/components', {}, { useAuth: true });
            if (Array.isArray(componentsData)) {
              const featureIds = features.map(f => f.id);
              const filtered = componentsData.filter((c: Component) => featureIds.includes(c.featureId));
              setComponents(filtered);
            }
          }}
          showMessage={showMessage}
          setMessage={setMessage}
          projectCode={selectedProduct?.projectId}
        />
      )}


      {/* Create Feature Modal */}
      {showCreateFeatureModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowCreateFeatureModal(false)}
        >
          <div
            className="card"
            style={{ maxWidth: '500px', width: '90%', zIndex: 1001 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Create New Feature</h3>
            <div className="form-group">
              <label>Feature Name *</label>
              <input
                type="text"
                value={newFeatureName}
                onChange={(e) => setNewFeatureName(e.target.value)}
                placeholder="Enter feature name"
                style={{ width: '100%' }}
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={newFeatureDescription}
                onChange={(e) => setNewFeatureDescription(e.target.value)}
                placeholder="Enter feature description"
                rows={3}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              <button
                onClick={handleCreateFeature}
                disabled={!newFeatureName.trim()}
                style={{
                  padding: '0.75rem 2rem',
                  backgroundColor: 'var(--primary)',
                  color: 'var(--bg-color)',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: newFeatureName.trim() ? 'pointer' : 'not-allowed',
                  opacity: newFeatureName.trim() ? 1 : 0.5
                }}
              >
                Create
              </button>
              <button
                onClick={() => {
                  setShowCreateFeatureModal(false);
                  setNewFeatureName('');
                  setNewFeatureDescription('');
                }}
                style={{
                  padding: '0.75rem 2rem',
                  backgroundColor: 'var(--surface-color)',
                  color: 'var(--text-color)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Component Modal */}
      {showCreateComponentModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowCreateComponentModal(false)}
        >
          <div
            className="card"
            style={{ maxWidth: '500px', width: '90%', zIndex: 1001 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Create New Component</h3>
            <div className="form-group">
              <label>Component Name *</label>
              <input
                type="text"
                value={newComponentName}
                onChange={(e) => setNewComponentName(e.target.value)}
                placeholder="Enter component name"
                style={{ width: '100%' }}
              />
            </div>
            <div className="form-group">
              <label>Feature *</label>
              <select
                value={newComponentFeatureId}
                onChange={(e) => setNewComponentFeatureId(e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="">Select a feature</option>
                {features.map(feature => (
                  <option key={feature.id} value={feature.id}>{feature.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={newComponentDescription}
                onChange={(e) => setNewComponentDescription(e.target.value)}
                placeholder="Enter component description"
                rows={3}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              <button
                onClick={handleCreateComponent}
                disabled={!newComponentName.trim() || !newComponentFeatureId}
                style={{
                  padding: '0.75rem 2rem',
                  backgroundColor: 'var(--primary)',
                  color: 'var(--bg-color)',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (newComponentName.trim() && newComponentFeatureId) ? 'pointer' : 'not-allowed',
                  opacity: (newComponentName.trim() && newComponentFeatureId) ? 1 : 0.5
                }}
              >
                Create
              </button>
              <button
                onClick={() => {
                  setShowCreateComponentModal(false);
                  setNewComponentName('');
                  setNewComponentDescription('');
                  setNewComponentFeatureId('');
                }}
                style={{
                  padding: '0.75rem 2rem',
                  backgroundColor: 'var(--surface-color)',
                  color: 'var(--text-color)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Plan Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDeleteTestPlan !== null}
        title="Delete Test Plan"
        message={`Are you sure you want to delete "${confirmDeleteTestPlan?.testPlanTitle}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteTestPlanAction}
        onCancel={() => setConfirmDeleteTestPlan(null)}
        danger={true}
      />

      {/* Create Session Modal */}
      <CreateSessionModal
        isOpen={showCreateSessionModal}
        onClose={() => {
          setShowCreateSessionModal(false);
          setSessionInitialData(undefined);
        }}
        initialData={sessionInitialData}
        onSessionCreated={async (session: Session) => {
          setShowCreateSessionModal(false);
          setSessionInitialData(undefined);
          // Only navigate to session runner if session was started (status: 'inprogress')
          // If status is 'todo', it means "Save for Later" was clicked, so don't navigate
          if (session.status === 'inprogress') {
            navigate(`/session/${session.id}`);
          }
        }}
      />
    </div>
  );
};

export default TestPlanManagement;

